import { PillCellRenderer } from '../_custom/DataTable/components/CellRenderers/PillCellRenderer/PillCellRenderer';
import styles from './DirectorStatus.module.scss';

export type DirectorStatus = 'ACTIVE' | 'FORMER' | null;

const STATUS_LABEL: Record<Exclude<DirectorStatus, null>, string> = {
    ACTIVE: 'Active',
    FORMER: 'Former',
};

/**
 * The single Active/Former decision shared by Management Profile (via
 * `ManagementProfileStatusPillRenderer`), Regulatory Checks (`RiskIndicatorRows`), and Credit
 * Bureau (`KeyOfficialsCreditSummary`). Returns `null` (render nothing) when `isActive` is absent;
 * each caller decides what "nothing" looks like in its own layout (a table cell's dash vs. simply
 * not rendering anything beside a name).
 *
 * The status keys off `isActive` alone, not the director's `source`: a vendor DIN refresh writes a
 * verified `isActive` onto a hand-added (COMPANY_USER/COAPPLICANT) row while keeping its source, and
 * a director that was never verified has no `isActive` at all.
 */
export function resolveDirectorStatus(isActive?: boolean | null): DirectorStatus {
    if (isActive === null || isActive === undefined) return null;
    return isActive ? 'ACTIVE' : 'FORMER';
}

/** Management Profile's table-cell presentation — a filled pill. */
export function DirectorStatusPill({ isActive }: { isActive?: boolean | null }) {
    const status = resolveDirectorStatus(isActive);
    if (!status) return null;
    return <PillCellRenderer label={STATUS_LABEL[status]} isActive={status === 'ACTIVE'} inactiveTone="risk" />;
}

/**
 * Regulatory Checks' and Credit Bureau's presentation (per Figma) — plain coloured text,
 * not a pill, meant to sit as the last segment of the designation/shareholding sub-line (the
 * caller is responsible for the ` • ` separator, since it only belongs there when the sub-line
 * already has text before it). Takes the already-resolved status so callers that also need it
 * for a render guard only call `resolveDirectorStatus` once.
 */
export function DirectorStatusText({ status }: { status: DirectorStatus }) {
    if (!status) return null;
    return <span className={status === 'ACTIVE' ? styles.Active : styles.Former}>{STATUS_LABEL[status]}</span>;
}
